import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge } from "@/components/ui/data";
import { Input, Select } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { useToast } from "@/app/Toast";
import { cn } from "@/lib/utils";
import { Ban, RefreshCw, RotateCcw, Send } from "lucide-react";

interface NotificationRow {
  id: number; bookingId: number | null; customerId: number | null;
  channel: "EMAIL" | "SMS" | "WEBHOOK"; eventType: string;
  scheduledAt: string; status: "PENDING" | "PROCESSING" | "SENT" | "FAILED" | "DEAD" | "DISMISSED";
  attemptCount: number; nextAttemptAt: string | null;
  providerMessageId: string | null;
  destinationMasked: string | null;
  failureText: string | null;
}

const STATUS_COLORS: Record<NotificationRow["status"], string> = {
  PENDING: "bg-muted text-muted-foreground",
  PROCESSING: "bg-blue-100 text-blue-900",
  SENT: "bg-emerald-100 text-emerald-900",
  FAILED: "bg-amber-100 text-amber-900",
  DEAD: "bg-rose-100 text-rose-900",
  DISMISSED: "bg-slate-100 text-slate-600",
};

export function NotificationsPage() {
  const { toast } = useToast();
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [status, setStatus] = useState<string>("");
  const [bookingId, setBookingId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [retrigger, setRetrigger] = useState(0);
  const [actingId, setActingId] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (bookingId) params.set("bookingId", bookingId);
      const data = await api.get<NotificationRow[]>(`/api/v1/notifications?${params.toString()}`);
      setRows(data);
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [status, bookingId, retrigger]);

  async function runWorker() {
    try {
      const r = await api.post<{ notifications: { sent: number; failed: number; dead: number } }>(
        "/api/v1/notifications/run-worker",
        { maxPerKind: 50 },
      );
      const n = r.notifications;
      toast("success", `Worker run: ${n.sent} sent, ${n.failed} failed, ${n.dead} dead`);
      await load();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Worker run failed");
    }
  }

  async function updateNotification(id: number, action: "retry" | "dismiss") {
    setActingId(id);
    try {
      await api.post(`/api/v1/notifications/${id}/${action}`);
      toast("success", action === "retry" ? "Notification queued for retry" : "Notification dismissed");
      await load();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : `Failed to ${action} notification`);
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Notifications</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
          <Button onClick={runWorker}><Send className="h-3.5 w-3.5" /> Run worker now</Button>
        </div>
      </div>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="PENDING">PENDING</option>
              <option value="PROCESSING">PROCESSING</option>
              <option value="SENT">SENT</option>
              <option value="FAILED">FAILED (will retry)</option>
              <option value="DEAD">DEAD (give up)</option>
              <option value="DISMISSED">DISMISSED</option>
            </Select>
            <Input
              type="number"
              placeholder="Filter by booking ID"
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              className="w-44"
            />
            <button onClick={() => setRetrigger((n) => n + 1)} className="text-xs text-muted-foreground hover:underline">
              Apply
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loading && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Booking</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Attempts</TableHead>
                  <TableHead>Next</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((n) => (
                  <TableRow key={n.id}>
                    <TableCell>{formatDateTime(n.scheduledAt)}</TableCell>
                    <TableCell>
                      {n.bookingId ? (
                        <Link to={`/bookings/${n.bookingId}`} className="text-primary hover:underline">#{n.bookingId}</Link>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell><Badge>{n.channel}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{n.eventType}</TableCell>
                    <TableCell className="font-mono text-xs">{n.destinationMasked ?? "—"}</TableCell>
                    <TableCell>
                      <span className={cn("rounded px-2 py-0.5 text-[10px] font-semibold", STATUS_COLORS[n.status])}>{n.status}</span>
                      {n.failureText && <p className="mt-1 max-w-xs truncate text-xs text-rose-700">{n.failureText}</p>}
                    </TableCell>
                    <TableCell>{n.attemptCount}</TableCell>
                    <TableCell className="text-muted-foreground">{n.nextAttemptAt ? formatDateTime(n.nextAttemptAt) : "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {(n.status === "FAILED" || n.status === "DEAD") && (
                          <Button variant="outline" disabled={actingId === n.id} onClick={() => void updateNotification(n.id, "retry")}>
                            <RotateCcw className="h-3.5 w-3.5" /> Retry
                          </Button>
                        )}
                        {(n.status === "PENDING" || n.status === "FAILED" || n.status === "DEAD") && (
                          <Button variant="outline" disabled={actingId === n.id} onClick={() => void updateNotification(n.id, "dismiss")}>
                            <Ban className="h-3.5 w-3.5" /> Dismiss
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground">
                      No notifications match these filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
